import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Typography, Stack } from '@mui/material'
import {
  CommonButton,
  CommonSelect,
  CommonDatePicker,
  CommonTextField,
  CommonRadio,
} from '@/components/common'
import { useGetProcedureTypes } from '@/hooks'
import { useDonorContext } from '@/context'
import { ROUTES, DONOR_PORTAL_SCREEN_CONFIG_CODE } from '@/constants'
import {
  DonorPortalScreenMessage,
  getEligibilityDateHelperText,
} from '@/components/donation'
import {
  getSearchByZipCodeParams,
  getSuggestedDonationDateFromProcedureEligibility,
  getEarliestSuggestedDonationDateFromEligibilities,
  formatZipAsTyped,
  isValidUsZip,
  isDateRangeSearchValid,
  ZIP_CODE_ERROR_MESSAGE,
  getDonorProfileZipCode,
} from '@/utils'

const YES_NO_ITEMS = [
  { id: 'true', label: 'Yes' },
  { id: 'false', label: 'No' },
]

export const SearchByZipCode = () => {
  const navigate = useNavigate()
  const { donor, getDonor } = useDonorContext()
  const { procedureTypes, loadingProcedureTypes, getProcedureTypes } =
    useGetProcedureTypes()

  const [formData, setFormData] = useState({
    zip_code: '',
    start_date: null,
    end_date: null,
    procedure_type_id: '',
    include_mobile_drives: true,
    include_donor_centers: true,
  })
  const [zipTouched, setZipTouched] = useState(false)
  const [zipFocused, setZipFocused] = useState(false)
  const zipInputRef = useRef(null)

  useEffect(() => {
    getProcedureTypes()
  }, [])

  useEffect(() => {
    getDonor()
  }, [getDonor])

  useEffect(() => {
    if (!donor?.id || formData.zip_code?.trim()) return
    const donorZip = getDonorProfileZipCode(donor)
    if (!donorZip) return
    setFormData(s => (s.zip_code ? s : { ...s, zip_code: donorZip }))
  }, [donor?.id, donor?.zip_code, donor?.address?.zip_code, formData.zip_code])

  useEffect(() => {
    const pid = formData.procedure_type_id
    if (!donor?.procedure_eligibilities?.length) return
    const suggested = pid
      ? getSuggestedDonationDateFromProcedureEligibility(
          donor.procedure_eligibilities,
          pid
        )
      : getEarliestSuggestedDonationDateFromEligibilities(
          donor.procedure_eligibilities
        )
    if (!suggested) return
    setFormData(s =>
      s.start_date != null
        ? s
        : { ...s, start_date: suggested, end_date: s.end_date }
    )
  }, [donor?.procedure_eligibilities, formData.procedure_type_id])

  const showZipError =
    zipTouched &&
    !zipFocused &&
    !!formData.zip_code?.trim() &&
    !isValidUsZip(formData.zip_code)
  const zipErrorMessage = showZipError ? ZIP_CODE_ERROR_MESSAGE : ''

  const handleSearch = () => {
    setZipTouched(true)
    if (!isValidUsZip(formData.zip_code)) {
      zipInputRef.current?.blur?.()
      document.getElementById('sbz-zip')?.blur?.()
      return
    }
    const params = getSearchByZipCodeParams(formData)
    navigate(`${ROUTES.DONATE_ZIP_CODE_RESULTS}?${params.toString()}`)
  }

  const isDisabled = useMemo(() => {
    if (!formData.zip_code?.trim() || !isValidUsZip(formData.zip_code))
      return true
    if (!isDateRangeSearchValid(formData.start_date, formData.end_date))
      return true
    if (!formData.include_mobile_drives && !formData.include_donor_centers)
      return true
    return false
  }, [formData])

  return (
    <Stack spacing={2} sx={{ pb: 1 }}>
      <DonorPortalScreenMessage
        code={DONOR_PORTAL_SCREEN_CONFIG_CODE.SEARCH_BY_ZIP_CODE_SEARCH}
      />
      <Box>
        <Typography variant='subtitle1' sx={{ mb: 0.5 }}>
          What is your ZIP code?
        </Typography>
        <CommonTextField
          id='sbz-zip'
          label='ZIP code'
          value={formData.zip_code}
          inputRef={zipInputRef}
          setValue={v =>
            setFormData(s => ({ ...s, zip_code: formatZipAsTyped(v) }))
          }
          onFocus={() => setZipFocused(true)}
          onBlur={() => {
            setZipFocused(false)
            setZipTouched(true)
          }}
          error={!!zipErrorMessage}
          helperText={zipErrorMessage}
          required
          inputProps={{
            maxLength: 5,
            inputMode: 'numeric',
            pattern: '[0-9]*',
            autoComplete: 'postal-code',
          }}
        />
      </Box>

      <Box>
        <Typography variant='subtitle1' sx={{ mb: 0.5 }}>
          When do you want to donate?
        </Typography>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems='flex-start'
        >
          <Box sx={{ flex: 1, width: '100%', minWidth: 0 }}>
            <CommonDatePicker
              id='sbz-start-date'
              label='Start date'
              value={formData.start_date}
              onChange={d => setFormData(s => ({ ...s, start_date: d }))}
              disablePast
              required
              helperText={getEligibilityDateHelperText({
                donorId: donor?.id,
                procedureEligibilities: donor?.procedure_eligibilities,
                procedureTypeId: formData.procedure_type_id,
                startDate: formData.start_date,
              })}
            />
          </Box>
          <Box sx={{ flex: 1, width: '100%', minWidth: 0 }}>
            <CommonDatePicker
              id='sbz-end-date'
              label='End date'
              value={formData.end_date}
              onChange={d => setFormData(s => ({ ...s, end_date: d }))}
              disablePast
              helperText='Leave blank to show all future dates'
            />
          </Box>
        </Stack>
      </Box>

      <Box>
        <Typography variant='subtitle1' sx={{ mb: 0.5 }}>
          What do you want to donate?
        </Typography>
        <CommonSelect
          id='sbz-procedures'
          label='Procedures'
          placeholder='All Available'
          enableSort={false}
          options={procedureTypes}
          value={formData.procedure_type_id}
          loading={loadingProcedureTypes}
          onChange={v => {
            const procedure_type_id = v || ''
            setFormData(s => {
              const next = { ...s, procedure_type_id }
              if (!procedure_type_id) return next
              const suggested =
                getSuggestedDonationDateFromProcedureEligibility(
                  donor?.procedure_eligibilities,
                  procedure_type_id
                )
              if (suggested) {
                if (!next.start_date) next.start_date = suggested
                if (!next.end_date && procedure_type_id) {
                  next.end_date = suggested
                }
              }
              return next
            })
          }}
        />
      </Box>

      <Box
        sx={{
          '& .MuiFormControlLabel-root': { mr: 2.5 },
        }}
      >
        <CommonRadio
          id='sbz-include-drives'
          label='Include mobile drives?'
          row
          value={formData.include_mobile_drives ? 'true' : 'false'}
          setValue={v =>
            setFormData(s => ({
              ...s,
              include_mobile_drives: v === 'true',
            }))
          }
          items={YES_NO_ITEMS}
        />
      </Box>

      <Box
        sx={{
          '& .MuiFormControlLabel-root': { mr: 2.5 },
        }}
      >
        <CommonRadio
          id='sbz-include-centers'
          label='Include donor centers?'
          row
          value={formData.include_donor_centers ? 'true' : 'false'}
          setValue={v =>
            setFormData(s => ({
              ...s,
              include_donor_centers: v === 'true',
            }))
          }
          items={YES_NO_ITEMS}
        />
      </Box>

      <Box pt={3} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <CommonButton
          id='sbz-cta'
          label='Search'
          variant='primary'
          color='primary'
          size='large'
          disabled={isDisabled}
          onClick={handleSearch}
        />
      </Box>
    </Stack>
  )
}

export default SearchByZipCode
